import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, ProgressBar, BigStat, SectionLabel } from '../../tcn/ui-kit';
import { TCN } from '../../tcn/ui-kit/tokens';
import { formatUSD, formatInt, formatPct } from '../shared/format';

/**
 * Module 5 — A5.2-A5.5 bonus detail screens, pixel-faithful to mockup
 * `module5-payout.jsx`:
 *   - rankup     (A5.2): earned/potential + remaining + Upgrades/Maintains
 *   - activeness (A5.3): earned/potential + per-level breakdown (n creators · ratio)
 *   - revenue    (A5.4): current pay-out + Path-to-100% + full-threshold card
 *   - recruitment(A5.5): earned/potential + MTD recruited / T+14 hit / T+30 rate
 *
 * Data-backed from manager_current_month (+ activeness_config ratios). Where a
 * per-component $ split isn't stored (e.g. per-level activeness $), shows the
 * available counts/ratios rather than fabricating figures.
 *
 * Props: kind, data (manager_current_month row), usdRate, activenessRatios {level: ratio}
 */
const ACCENT = { rankup: TCN.cyan, activeness: TCN.green, revenue: TCN.diamond, recruitment: TCN.magenta };
const LEVEL_COLOR = { 5: TCN.diamond, 4: TCN.green, 3: TCN.cyan, 2: TCN.amber, 1: TCN.red };

const BonusDetail = ({ kind, data, usdRate = 0.01, activenessRatios = {} }) => {
  const { t } = useTranslation();
  if (!data) return null;
  const accent = ACCENT[kind] ?? TCN.cyan;
  const usd = (clear) => (clear == null ? 0 : Number(clear) * usdRate);

  const miniCard = { padding: 14 };
  const miniLabel = { fontSize: 10, color: TCN.textMute, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px' };

  // ---- rankup (A5.2) ----
  if (kind === 'rankup') {
    const earned = usd(data.rankup_bonus_earned_clear);
    const potential = usd(data.rankup_bonus_potential_clear);
    const remaining = Math.max(0, potential - earned);
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <Card accent={accent}>
          <BigStat label={t('tcn.m5.detail.earned', 'Earned')} value={formatUSD(earned)} sub={t('tcn.m5.detail.ofPotential', 'of {{v}} potential', { v: formatUSD(potential) })} />
          <div style={{ marginTop: 12 }}>
            <ProgressBar value={earned} target={potential || 1} color={accent} animated />
            <div style={{ marginTop: 6, fontSize: 11, color: accent }}>
              {formatUSD(remaining)} {t('tcn.m5.detail.remaining', 'remaining')}
            </div>
          </div>
        </Card>
        <SectionLabel>{t('tcn.m5.bonus.rankup.upgrades', 'Upgrades')}</SectionLabel>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <Card style={miniCard}>
            <div style={miniLabel}>{t('tcn.m5.detail.achieved', 'Achieved')}</div>
            <div style={{ fontSize: 26, fontWeight: 700, color: TCN.green, marginTop: 4, fontVariantNumeric: 'tabular-nums' }}>{formatInt(data.upgrade_count)}</div>
          </Card>
          <Card style={miniCard}>
            <div style={miniLabel}>{t('tcn.m5.bonus.rankup.maintain', 'Maintained')}</div>
            <div style={{ fontSize: 26, fontWeight: 700, color: accent, marginTop: 4, fontVariantNumeric: 'tabular-nums' }}>{formatInt(data.maintain_count)}</div>
          </Card>
        </div>
      </div>
    );
  }

  // ---- activeness (A5.3) ----
  if (kind === 'activeness') {
    const earned = usd(data.activeness_bonus_earned_clear);
    const potential = usd(data.activeness_bonus_potential_clear);
    const levels = [5, 4, 3, 2, 1].map((lvl) => ({
      lvl,
      n: Number(data[`creators_at_level_${lvl}`] ?? 0),
      ratio: activenessRatios[lvl],
    }));
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <Card accent={accent}>
          <BigStat label={t('tcn.m5.detail.earned', 'Earned')} value={formatUSD(earned)} sub={t('tcn.m5.detail.ofPotential', 'of {{v}} potential', { v: formatUSD(potential) })} />
          <div style={{ marginTop: 12 }}>
            <ProgressBar value={earned} target={potential || 1} color={accent} animated />
          </div>
        </Card>
        <SectionLabel>{t('tcn.m5.detail.byLevel', 'By activeness level')}</SectionLabel>
        {levels.map((l) => (
          <div key={l.lvl} style={{ padding: 12, background: TCN.surface1, border: `1px solid ${TCN.border}`, borderRadius: 12, display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 38, height: 38, borderRadius: 10, background: `${LEVEL_COLOR[l.lvl]}1f`, color: LEVEL_COLOR[l.lvl], display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 12 }}>
              L{l.lvl}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: TCN.text }}>
                {t('tcn.m5.detail.nCreators', '{{n}} creators', { n: l.n })}
              </div>
              <div style={{ fontSize: 11, color: TCN.textMute, marginTop: 2 }}>
                {l.ratio != null ? t('tcn.m5.detail.ratio', '{{r}}× bonus ratio', { r: l.ratio }) : '—'}
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  // ---- revenue (A5.4) ----
  if (kind === 'revenue') {
    const current = usd(data.revenue_bonus_current_clear);
    const full = usd(data.revenue_bonus_full_threshold_clear);
    const completion = Number(data.diamonds_completion_pct_vs_monthly ?? 0);
    const unlocked = Math.max(0, full - current);
    const clamped = Math.max(0, Math.min(100, completion));
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <Card accent={accent}>
          <BigStat label={t('tcn.m5.detail.current', 'Current pay-out')} value={formatUSD(current)} sub={t('tcn.m5.detail.basedOn', 'based on {{p}} completion', { p: formatPct(completion) })} />
        </Card>
        <Card>
          <div style={miniLabel}>{t('tcn.m5.detail.pathTo100', 'Path to 100%')}</div>
          <div style={{ marginTop: 18, position: 'relative', height: 8, background: TCN.surface3, borderRadius: 4 }}>
            <div style={{ position: 'absolute', insetInlineStart: 0, top: 0, height: 8, width: `${clamped}%`, background: accent, borderRadius: 4 }} />
            <div style={{ position: 'absolute', insetInlineStart: `${clamped}%`, top: -6, width: 2, height: 20, background: TCN.text }} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 12, fontSize: 11, color: TCN.textMute }}>
            <span>0%</span><span>50%</span><span>100%</span>
          </div>
          <div style={{ marginTop: 14, padding: 12, background: TCN.surface2, borderRadius: 10 }}>
            <div style={miniLabel}>{t('tcn.m5.detail.at100', 'At 100% completion')}</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: accent, marginTop: 4, fontVariantNumeric: 'tabular-nums' }}>{formatUSD(full)}</div>
            <div style={{ fontSize: 11, color: TCN.textDim, marginTop: 2 }}>
              {t('tcn.m5.detail.unlocked', '+{{v}} unlocked at full target', { v: formatUSD(unlocked) })}
            </div>
          </div>
        </Card>
      </div>
    );
  }

  // ---- recruitment (A5.5) ----
  const earned = usd(data.recruitment_bonus_current_clear);
  const potential = usd(data.recruitment_bonus_potential_clear);
  const t30 = data.recruitment_t30_rate;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <Card accent={accent}>
        <BigStat label={t('tcn.m5.detail.earned', 'Earned')} value={formatUSD(earned)} sub={t('tcn.m5.detail.ofPotential', 'of {{v}} potential', { v: formatUSD(potential) })} />
        <div style={{ marginTop: 12 }}>
          <ProgressBar value={earned} target={potential || 1} color={accent} animated />
        </div>
      </Card>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        <Card style={miniCard}>
          <div style={miniLabel}>{t('tcn.m5.detail.mtdRecruited', 'MTD recruited')}</div>
          <div style={{ fontSize: 26, fontWeight: 700, marginTop: 4, color: TCN.text, fontVariantNumeric: 'tabular-nums' }}>{formatInt(data.recruitment_mtd_count)}</div>
        </Card>
        <Card style={miniCard}>
          <div style={miniLabel}>{t('tcn.m5.detail.t14hit', 'T+14 hit')}</div>
          <div style={{ fontSize: 26, fontWeight: 700, color: TCN.green, marginTop: 4, fontVariantNumeric: 'tabular-nums' }}>{formatInt(data.recruitment_t14_count)}</div>
        </Card>
        <Card style={{ ...miniCard, gridColumn: 'span 2' }}>
          <div style={miniLabel}>{t('tcn.m5.detail.t30rate', 'T+30 achievement rate')}</div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginTop: 6 }}>
            <span style={{ fontSize: 26, fontWeight: 700, color: TCN.amber, fontVariantNumeric: 'tabular-nums' }}>{t30 == null ? '—' : formatPct(t30)}</span>
            <span style={{ fontSize: 12, color: TCN.textMute }}>{t('tcn.m5.detail.target70', 'target 70%')}</span>
          </div>
          {t30 != null && <ProgressBar value={Number(t30)} target={70} color={TCN.amber} animated />}
        </Card>
      </div>
    </div>
  );
};

export default BonusDetail;
